import { useEffect, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/42926a9a-fbe1-4170-a082-9346a2f60ed9/images/29895b281a0899767d202460eabcd2184bb15151831f29aab1205a0768bf52ef.png";
const PORTRAIT =
  "https://customer-assets.emergentagent.com/job_symmetry-studio-1/artifacts/1f2vcu2p_prat.jpeg";
const STAMP =
  "https://static.prod-images.emergentagent.com/jobs/42926a9a-fbe1-4170-a082-9346a2f60ed9/images/536129656e41e129358aec259db8c51d1e52e90b93976a1cba6fcd3b71fb7841.png";

/* ------------------------------- Hooks ------------------------------- */
function useReveal() {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.08 }
    );

    const observed = new WeakSet();
    const observeAll = () => {
      document.querySelectorAll(".reveal").forEach((el) => {
        if (!observed.has(el)) {
          observed.add(el);
          io.observe(el);
        }
      });
    };
    observeAll();

    const mo = new MutationObserver(observeAll);
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, []);
}

/* ----------------------------- Primitives ---------------------------- */
function FleurDivider({ label }) {
  const Side = ({ flip = false }) => (
    <svg
      viewBox="0 0 200 14"
      preserveAspectRatio="none"
      style={flip ? { transform: "scaleX(-1)" } : undefined}
    >
      <g stroke="currentColor" fill="none" strokeWidth="1">
        <line x1="0" y1="7" x2="120" y2="7" />
        <line x1="0" y1="4" x2="120" y2="4" />
        <line x1="0" y1="10" x2="120" y2="10" />
        <circle cx="135" cy="7" r="2.5" fill="currentColor" stroke="none" />
        <path
          d="M150 7 q5 -6 12 0 q-5 6 -12 0 Z"
          fill="currentColor"
          stroke="none"
        />
        <circle cx="170" cy="7" r="1.5" fill="currentColor" stroke="none" />
        <line x1="175" y1="7" x2="200" y2="7" />
      </g>
    </svg>
  );
  return (
    <div className="fleur my-6 mx-auto">
      <Side />
      {label ? (
        <span className="font-chapter italic text-[var(--oxblood)] text-sm tracking-[0.4em] uppercase whitespace-nowrap">
          {label}
        </span>
      ) : (
        <span className="diamond" />
      )}
      <Side flip />
    </div>
  );
}

function Ornament({ label }) {
  return <FleurDivider label={label} />;
}

function ChapterTag({ numeral, title, kicker, act, tone = "default" }) {
  const toneCfg = {
    default: { medallion: "", kicker: "text-[var(--rose)]" },
    mendl: { medallion: "is-mendl", kicker: "text-[var(--mendl)]" },
    sage: { medallion: "is-sage", kicker: "text-[var(--sage)]" },
    butter: { medallion: "is-butter", kicker: "text-[#9a7a30]" },
    peacock: { medallion: "is-peacock", kicker: "text-[var(--peacock)]" },
    saffron: { medallion: "is-saffron", kicker: "text-[var(--saffron)]" },
  }[tone] || { medallion: "", kicker: "text-[var(--rose)]" };
  return (
    <div className="flex flex-col items-center text-center mb-10 reveal">
      <span className={`medallion mb-5 ${toneCfg.medallion}`}>{numeral}</span>
      <span className={`font-chapter italic ${toneCfg.kicker} tracking-[0.5em] text-xs uppercase`}>
        {act ? act : `Chapter ${numeral}`}
        {kicker ? ` · ${kicker}` : ""}
      </span>
      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-2 text-[var(--ink)]">
        {title}
      </h2>
      <FleurDivider />
    </div>
  );
}

/* --------------------------- Top Bulletin ---------------------------- */
function TopBulletin() {
  const today = new Date();
  const dateStr = today.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  // Stable edition number derived from year × ordinal day for vintage feel
  const start = new Date(today.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((today - start) / 86400000);
  const edition = String(1280 + dayOfYear).padStart(4, "0");

  return (
    <div
      data-testid="top-bulletin"
      className="bulletin"
      role="status"
      aria-label="Daily notice to visitors"
    >
      <div className="max-w-6xl mx-auto px-6 py-2 flex items-center justify-between gap-6">
        <span>The Bureau Bulletin · Edition № {edition}</span>
        <span className="hidden md:inline">
          {dateStr}
          <span className="bullet" />
          Notice to Visitors
          <span className="bullet" />
          Briefs accepted by lobby boy
        </span>
        <span className="hidden lg:inline">Weather · Fair, with light pitch</span>
      </div>
    </div>
  );
}

/* -------------------------------- Nav -------------------------------- */
function Nav() {
  const [open, setOpen] = useState(false);
  const links = [
    { href: "#about", label: "I · About" },
    { href: "#case-studies", label: "II · Case Studies" },
    { href: "#press", label: "III · Press" },
    { href: "#menu", label: "IV · Services" },
    { href: "#contact", label: "V · Contact" },
  ];
  return (
    <nav
      data-testid="top-nav"
      className="sticky top-0 z-40 paper-bg border-b-2 border-[var(--oxblood)]"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-6 py-3 flex items-center justify-between gap-3">
        <a
          href="#top"
          data-testid="nav-brand"
          className="font-chapter italic text-[var(--oxblood)] text-[0.62rem] sm:text-sm tracking-[0.28em] sm:tracking-[0.35em] uppercase truncate"
        >
          The Bureau of Considered Communications
        </a>
        <ul className="hidden md:flex items-center gap-6 font-mono-w text-[var(--ink)] text-xs tracking-[0.22em] uppercase whitespace-nowrap">
          {links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                data-testid={`nav-${l.href.slice(1)}`}
                className="hover:text-[var(--oxblood)] transition-colors whitespace-nowrap"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>
        <button
          type="button"
          data-testid="nav-burger"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="md:hidden flex items-center justify-center w-9 h-9 border-2 border-[var(--oxblood)] bg-[var(--paper)] text-[var(--oxblood)] shrink-0"
        >
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
            {open ? (
              <g stroke="currentColor" strokeWidth="1.6">
                <line x1="3" y1="3" x2="13" y2="13" />
                <line x1="13" y1="3" x2="3" y2="13" />
              </g>
            ) : (
              <g stroke="currentColor" strokeWidth="1.6">
                <line x1="2" y1="4" x2="14" y2="4" />
                <line x1="2" y1="8" x2="14" y2="8" />
                <line x1="2" y1="12" x2="14" y2="12" />
              </g>
            )}
          </svg>
        </button>
      </div>
      {open && (
        <ul
          data-testid="nav-mobile-list"
          className="md:hidden border-t-2 border-[var(--oxblood)] bg-[var(--paper-deep)] px-5 py-3 space-y-2 font-mono-w text-[var(--ink)] text-xs tracking-[0.22em] uppercase"
        >
          {links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                data-testid={`nav-mobile-${l.href.slice(1)}`}
                onClick={() => setOpen(false)}
                className="block py-2 border-b border-dotted border-[var(--oxblood)] hover:text-[var(--oxblood)]"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>
      )}
    </nav>
  );
}

/* -------------------------------- Hero ------------------------------- */
function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative paper-bg overflow-hidden"
    >
      <div className="max-w-5xl mx-auto px-6 pt-16 pb-24 flex flex-col items-center text-center">
        <div className="reveal flex items-center justify-center gap-3 sm:gap-4 text-[var(--oxblood)]">
          <svg viewBox="0 0 80 6" className="w-10 sm:w-20 h-2" aria-hidden="true">
            <line x1="0" y1="3" x2="74" y2="3" stroke="currentColor" strokeWidth="1" />
            <circle cx="78" cy="3" r="2" fill="currentColor" />
          </svg>
          <span className="font-chapter italic text-[0.7rem] sm:text-sm tracking-[0.35em] sm:tracking-[0.55em] uppercase whitespace-nowrap">
            A Career In Five Acts
          </span>
          <svg viewBox="0 0 80 6" className="w-10 sm:w-20 h-2" aria-hidden="true">
            <circle cx="2" cy="3" r="2" fill="currentColor" />
            <line x1="6" y1="3" x2="80" y2="3" stroke="currentColor" strokeWidth="1" />
          </svg>
        </div>
        <h1
          data-testid="hero-title"
          className="font-display text-[3.2rem] sm:text-[5.4rem] lg:text-[7.5rem] leading-[0.92] text-[var(--ink)] reveal mt-8"
        >
          Prathiksha
          <br />
          <span className="italic text-[var(--oxblood)]">BU.</span>
        </h1>
        <p className="reveal mt-6 font-chapter italic text-[var(--ink-soft)] text-base sm:text-lg tracking-[0.18em] uppercase">
          Communications &amp; Public Relations
        </p>
        <div className="reveal mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[var(--rose)]">
          <span className="hidden sm:inline-block w-10 h-px bg-[var(--rose)]" />
          <span className="font-mono-w text-[0.62rem] sm:text-[0.65rem] tracking-[0.32em] sm:tracking-[0.45em] uppercase whitespace-nowrap">Est. MMXIX</span>
          <span className="diamond" />
          <span className="font-mono-w text-[0.62rem] sm:text-[0.65rem] tracking-[0.32em] sm:tracking-[0.45em] uppercase whitespace-nowrap">5+ Years on the Beat</span>
          <span className="hidden sm:inline-block w-10 h-px bg-[var(--rose)]" />
        </div>
        <div className="reveal mt-6 flex flex-wrap items-center justify-center gap-3 max-w-2xl">
          {[
            ["Tier-1 Media", "pill--mendl"],
            ["Thought Leadership", "pill--peacock"],
            ["Op-Eds & Bylines", "pill--saffron"],
            ["Crisis Comms", "pill--sage"],
          ].map(([t, cls]) => (
            <span
              key={t}
              className={`pill ${cls}`}
              data-testid={`hero-tag-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {t}
            </span>
          ))}
        </div>
        <Ornament />
      </div>
    </section>
  );
}

/* ------------------------------- About ------------------------------- */
function About() {
  const stats = [
    ["Title", "Communications & PR Manager"],
    ["Tenure", "Five years, and counting"],
    ["Beat", "Tech · Finance · AI · Cybersec · Web3"],
    ["Specialty", "Tier-1 narratives"],
  ];
  return (
    <section id="about" data-testid="about-section" className="paper-pink-bg py-24">
      <div className="max-w-5xl mx-auto px-6">
        <ChapterTag numeral="I" act="Act I" tone="mendl" kicker="Dramatis Personae" title="A Brief Personnel File" />
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10 lg:gap-14 items-start">
          <div className="reveal">
            <a
              href="https://www.linkedin.com/in/prathiksha-bu-1b58901b1/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-portrait-link"
              aria-label="Visit Prathiksha BU on LinkedIn"
              className="block"
            >
              <div className="frame">
                <img
                  src={PORTRAIT}
                  alt="Portrait of Prathiksha BU"
                  className="aspect-[4/5]"
                />
              </div>
              <p className="mt-3 text-center overline">No. 0042 · Plate II · /in/prathiksha-bu ↗</p>
            </a>
          </div>
          <div className="reveal">
            <div className="border-[2px] border-double border-[var(--oxblood)] p-5 sm:p-8 bg-[var(--paper-deep)]">
              <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                <span className="overline">Personnel Dossier</span>
                <span className="overline">Strictly Off-Record</span>
              </div>
              <h3 className="font-display text-3xl text-[var(--ink)]">
                Prathiksha BU
              </h3>
              <hr className="my-6 border-t-2 border-dotted border-[var(--oxblood)]" />
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 font-mono-w text-sm">
                {stats.map(([k, v]) => (
                  <div key={k} className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-0">
                    <dt className="text-[var(--oxblood)] uppercase tracking-[0.2em] text-xs whitespace-nowrap">
                      {k}
                    </dt>
                    <span className="leader hidden sm:block" />
                    <dd className="text-[var(--ink)] sm:text-right">{v}</dd>
                  </div>
                ))}
              </dl>
              <hr className="my-6 border-t-2 border-dotted border-[var(--oxblood)]" />
              <p className="text-[var(--ink-soft)] leading-relaxed">
                Former
                {" "}<span className="text-[var(--ink)]">Tech Correspondent</span>{" "}
                at{" "}
                <a
                  href="https://yourstory.com/author/prathiksha-bu"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-yourstory-link"
                  className="text-[var(--ink)] hover:text-[var(--oxblood)] underline decoration-[var(--mendl)] decoration-2 underline-offset-[4px]"
                >
                  YourStory ↗
                </a>.
              </p>
              <p className="text-[var(--ink-soft)] leading-relaxed mt-4">
                <span className="overline mr-2">Past Patrons</span>
                <span className="text-[var(--ink)]">SynFutures · WeFi · CakeWallet · Bitfinex · Nansen · Session · Tether · Foresight Ventures · Morph · Bittensor · Bitmind · Blockaid · Nym · O.xyz · Paybis · Bitget · GoMining.</span>
              </p>
              <hr className="my-6 border-t-2 border-dotted border-[var(--oxblood)]" />
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <div>
                  <p className="overline">Read at</p>
                  <p className="font-display text-lg text-[var(--ink)] mt-1 leading-snug">
                    MA · Journalism &amp; Communications
                  </p>
                  <p className="font-chapter italic text-[var(--ink-soft)]">
                    St Joseph&apos;s University
                  </p>
                </div>
                <span className="font-mono-w text-[0.65rem] tracking-[0.3em] uppercase text-[var(--oxblood)]">
                  2019 — 2021
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          data-testid="about-note"
          className="reveal mt-16 max-w-3xl mx-auto text-center"
        >
          <FleurDivider label="A note from me" />
          <p className="font-chapter italic text-2xl sm:text-3xl text-[var(--ink)] leading-snug mt-6">
            "The economy is shrinking, the world is on fire, and good PR is
            somehow still expected to fix everything — and that&apos;s the
            part of the job I actually enjoy."
          </p>
          <FleurDivider />
          <p className="overline mt-4">— Prathiksha</p>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- Coverage Modal ---------------------------- */
function CoverageModal({ item, onClose }) {
  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [item, onClose]);

  if (!item) return null;

  const grouped = (item.coverage || []).reduce((acc, c) => {
    (acc[c.outlet] = acc[c.outlet] || []).push(c);
    return acc;
  }, {});
  // Preserve insertion order from the backend (Bloomberg, Forbes, etc. first)
  const outlets = Object.entries(grouped);
  const total = (item.coverage || []).length;

  return (
    <div
      data-testid="coverage-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-8"
      style={{ background: "rgba(43, 28, 58, 0.6)" }}
      onClick={onClose}
    >
      <div
        className="relative bg-[var(--paper-deep)] border-[3px] border-double border-[var(--oxblood)] shadow-hard max-w-3xl w-full max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="coverage-close"
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 border-2 border-[var(--oxblood)] bg-[var(--paper)] text-[var(--oxblood)] font-mono-w text-lg hover:bg-[var(--pink)] transition-colors"
          aria-label="Close"
        >
          ×
        </button>
        <div className="p-6 sm:p-10">
          <div className="text-center">
            <span className="overline">Case Study · Press Coverage</span>
            <h3 className="font-display text-3xl sm:text-4xl text-[var(--ink)] mt-2">
              {item.client}
            </h3>
            <p className="font-chapter italic text-[var(--ink-soft)] mt-1">
              {item.tagline}
            </p>
            <FleurDivider />
          </div>

          <div className="mt-2 space-y-8">
            {outlets.map(([outlet, items]) => (
              <div
                key={outlet}
                data-testid={`coverage-outlet-${outlet.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="flex items-baseline gap-3 mb-3">
                  <h4 className="font-display text-xl text-[var(--ink)]">{outlet}</h4>
                  <span className="leader" />
                  <span className="overline">{items.length} {items.length === 1 ? "article" : "articles"}</span>
                </div>
                <ul className="space-y-3">
                  {items.map((c, i) => (
                    <li key={c.url}>
                      <a
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start gap-4 p-3 border border-[var(--oxblood)] bg-[var(--paper)] hover:bg-[var(--pink)] transition-colors no-underline"
                      >
                        <span className="font-mono-w text-[var(--oxblood)] text-xs mt-1 shrink-0 w-6">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <img
                          src={c.image}
                          alt=""
                          loading="lazy"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            e.currentTarget.style.visibility = "hidden";
                          }}
                          className="w-20 h-14 sm:w-24 sm:h-16 object-cover border border-[var(--oxblood)] shrink-0 bg-[var(--paper-deep)]"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="font-display text-sm sm:text-base text-[var(--ink)] leading-snug">
                            {c.title || c.url}
                          </p>
                          <p className="font-mono-w text-[0.65rem] tracking-[0.15em] uppercase text-[var(--oxblood)] mt-1 truncate">
                            {c.url.replace(/^https?:\/\//, "").split("/")[0]} ↗
                          </p>
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------- Case Studies --------------------------- */
function CaseStudies() {
  const [data, setData] = useState({ items: [], categories: ["All"] });
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);
  const [openItem, setOpenItem] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${API}/case-studies`, {
        params: active === "All" ? {} : { category: active },
      })
      .then((res) => {
        if (mounted) setData(res.data);
      })
      .catch(() => {
        if (mounted) toast.error("The archive is, regrettably, closed.");
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [active]);

  return (
    <section
      id="case-studies"
      data-testid="case-studies-section"
      className="paper-mint-bg py-24 border-t border-[var(--oxblood)]"
    >
      <div className="max-w-6xl mx-auto px-6">
        <ChapterTag
          numeral="II"
          act="Act II"
          tone="sage"
          kicker="From The Archives"
          title="Case Studies"
        />
        <div
          data-testid="case-filters"
          className="flex flex-wrap items-center justify-center gap-3 mb-12 reveal"
        >
          {data.categories.map((c, ci) => {
            const tones = ["pill--sage", "pill--mendl", "pill--peacock", "pill--saffron", "pill--butter", "pill--plum"];
            return (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase()}`}
                onClick={() => setActive(c)}
                className={`pill ${tones[ci % tones.length]} ${active === c ? "is-active" : ""}`}
              >
                {c}
              </button>
            );
          })}
        </div>

        {loading ? (
          <p
            data-testid="case-loading"
            className="text-center font-chapter italic text-[var(--ink-soft)]"
          >
            Pulling the files from the cabinet…
          </p>
        ) : data.items.length === 0 ? (
          <p
            data-testid="case-empty"
            className="text-center font-chapter italic text-[var(--ink-soft)]"
          >
            No case files in this drawer. Try another.
          </p>
        ) : (
          <div
            data-testid="case-grid"
            className="grid grid-cols-1 md:grid-cols-2 gap-10"
          >
            {data.items.map((item, i) => {
              const hasCoverage = (item.coverage || []).length > 0;
              return (
                <article
                  key={item.id}
                  data-testid={`case-item-${item.id}`}
                  onClick={() => hasCoverage && setOpenItem(item)}
                  className={`reveal relative overflow-hidden border-[2px] border-double border-[var(--oxblood)] bg-[var(--paper-deep)] p-6 shadow-hard-sm ${
                    hasCoverage ? "cursor-pointer hover:bg-[var(--pink)] transition-colors" : ""
                  }`}
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="overline">Study No. {String(i + 1).padStart(2, "0")}</span>
                    <span className="overline">{item.year}</span>
                  </div>
                  <div className="frame relative">
                    <img
                      src={item.image}
                      alt={`${item.client} case study`}
                      className="aspect-[16/9]"
                    />
                    {item.logo && (
                      <span className="absolute top-3 right-3 w-14 h-14 rounded-full border-2 border-[var(--oxblood)] bg-[var(--paper)] flex items-center justify-center shadow-hard-sm overflow-hidden">
                        <img
                          src={item.logo}
                          alt={`${item.client} logo`}
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            const dom = item.logo_domain || `${item.client.toLowerCase()}.com`;
                            e.currentTarget.src = `https://www.google.com/s2/favicons?sz=128&domain=${dom}`;
                          }}
                          className="w-10 h-10 object-contain"
                        />
                      </span>
                    )}
                  </div>
                  <div className="mt-5">
                    <span className="pill">{item.category}</span>
                    <h3 className="font-display text-3xl text-[var(--ink)] mt-3">
                      {item.client}
                    </h3>
                    <p className="font-chapter italic text-[var(--ink-soft)] mt-1">
                      {item.tagline}
                    </p>
                    <hr className="my-4 border-t border-dotted border-[var(--oxblood)]" />
                    <p className="text-[var(--ink)] text-sm leading-relaxed">
                      {item.summary}
                    </p>
                    <div className="mt-5">
                      <span className="overline">Notable placements</span>
                      <ul className="mt-2 space-y-1.5">
                        {item.results.map((r) => (
                          <li
                            key={r}
                            className="flex items-baseline font-mono-w text-sm"
                          >
                            <span className="diamond mr-3 shrink-0" />
                            <span className="text-[var(--ink)]">{r}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {hasCoverage && (
                      <div className="mt-5 flex items-center justify-end">
                        <button
                          data-testid={`case-open-${item.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenItem(item);
                          }}
                          className="pill"
                        >
                          Open study ↗
                        </button>
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
      <CoverageModal item={openItem} onClose={() => setOpenItem(null)} />
    </section>
  );
}

/* ----------------------------- Press Wall ---------------------------- */
function PressWall() {
  const [outlets, setOutlets] = useState([]);
  useEffect(() => {
    axios.get(`${API}/press`).then((r) => setOutlets(r.data.outlets || []));
  }, []);

  return (
    <section
      id="press"
      data-testid="press-section"
      className="paper-butter-bg py-24 border-t border-[var(--oxblood)]"
    >
      <div className="max-w-5xl mx-auto px-6">
        <ChapterTag
          numeral="III"
          act="Entr'acte"
          tone="butter"
          kicker="As Featured In · The Newsstand"
          title="The Press Cabinet"
        />
        <div
          data-testid="press-grid"
          className="grid grid-cols-2 sm:grid-cols-3 gap-0 reveal border-2 border-[var(--oxblood)] bg-[var(--paper)]"
        >
          {outlets.map((o, i) => {
            const isOrphan =
              i === outlets.length - 1 && outlets.length % 3 === 1;
            // Cycle through Wes palette + soft tile backgrounds
            const palette = [
              { bg: "var(--paper)", hover: "var(--mendl)" },
              { bg: "var(--paper-peacock)", hover: "var(--peacock-soft)" },
              { bg: "var(--paper-butter)", hover: "var(--butter)" },
              { bg: "var(--paper-mint)", hover: "var(--sage)" },
              { bg: "var(--paper-pink)", hover: "var(--mendl)" },
              { bg: "var(--paper-deep)", hover: "var(--pink)" },
            ];
            const tone = palette[i % palette.length];
            const inner = (
              <>
                <span className="overline block mb-1">№ {String(i + 1).padStart(2, "0")}</span>
                <span className="font-display text-lg sm:text-xl text-[var(--ink)] leading-tight">
                  {o.name}
                </span>
                {o.url && (
                  <span className="block mt-1 font-mono-w text-[0.6rem] tracking-[0.3em] uppercase text-[var(--oxblood)]">
                    Read article ↗
                  </span>
                )}
              </>
            );
            const cls =
              "press-tile border border-[var(--oxblood)] -m-px p-6 flex items-center justify-center text-center min-h-[110px] transition-colors" +
              (isOrphan ? " sm:col-span-3" : "");
            const style = { background: tone.bg, "--tile-hover": tone.hover };
            return o.url ? (
              <a
                key={o.name}
                data-testid={`press-outlet-${i}`}
                href={o.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`${cls} no-underline`}
                style={style}
              >
                <div>{inner}</div>
              </a>
            ) : (
              <div
                key={o.name}
                data-testid={`press-outlet-${i}`}
                className={cls}
                style={style}
              >
                <div>{inner}</div>
              </div>
            );
          })}
        </div>
        <Ornament label="& Many More By Request" />
      </div>
    </section>
  );
}

/* ------------------------------- Menu ------------------------------- */
function MenuCard() {
  const sections = [
    {
      title: "Aperitifs · The Quick Things",
      items: [
        ["Press Release & Statement", "Drafted, edited, distributed"],
        ["Media List Curation", "Built and maintained for the beat"],
        ["Reactive Quotes", "Same-day comment on breaking news"],
      ],
    },
    {
      title: "Main Courses · The Larger Affairs",
      items: [
        [
          "Op-Eds & Bylines",
          "Ghostwritten, pitched, placed",
          "https://www.coindesk.com/opinion/2025/03/27/the-usdlibra-debacle-political-endorsements-are-driving-rug-pulls",
        ],
        [
          "Thought Leadership Programmes",
          "Founder positioning, six to twelve months",
          "https://www.thestreet.com/crypto/innovation/cake-labs-ceo-believes-awareness-will-drive-bitcoin-privacy-tools",
        ],
        [
          "Tier-1 Media Strategy",
          "Targeted pitching to top business outlets",
          "https://cointelegraph.com/news/nym-co-founder-and-chelsea-manning-discuss-decentralization-and-privacy",
        ],
        ["Executive Visibility & Profiling", "Founder & executive interviews, prep, profiles"],
      ],
    },
    {
      title: "Digestifs · After The Fact",
      items: [
        [
          "Crisis & Issues Management",
          "Response strategy, statements, holding lines",
          "https://cointelegraph.com/news/nym-co-founder-and-chelsea-manning-discuss-decentralization-and-privacy",
        ],
        ["Policy & Regulatory Narratives", "Comment on regulation and policy moves"],
      ],
    },
  ];
  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="paper-peacock-bg py-24 border-t border-[var(--oxblood)]"
    >
      <div className="max-w-4xl mx-auto px-6">
        <ChapterTag
          numeral="IV"
          act="Intermezzo"
          tone="peacock"
          kicker="From The Concierge"
          title="A Menu Of Services"
        />
        <div className="flex justify-center -mb-3 reveal" aria-hidden="true">
          <span className="twine-bow"><span className="twine-knot" /></span>
        </div>
        <div
          data-testid="menu-card"
          className="reveal scallop-top border-[3px] border-double border-[var(--oxblood)] bg-[var(--paper-deep)] p-8 sm:p-12 shadow-hard"
        >
          <div className="text-center">
            <p className="overline">From the desk of</p>
            <h3 className="font-display text-3xl sm:text-4xl text-[var(--ink)] mt-2">
              Prathiksha BU · Communications
            </h3>
            <p className="font-chapter italic text-[var(--ink-soft)] mt-1">
              service · ten thirty until seven, every day except thursday
            </p>
            <Ornament />
          </div>
          {sections.map((sec) => (
            <div key={sec.title} className="mt-8">
              <h4 className="text-center font-chapter italic text-[var(--oxblood)] tracking-[0.32em] uppercase text-xs">
                {sec.title}
              </h4>
              <ul className="mt-4 space-y-4 sm:space-y-3">
                {sec.items.map(([name, desc, href]) => (
                  <li
                    key={name}
                    data-testid={`menu-item-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="flex flex-col sm:flex-row sm:items-baseline font-mono-w"
                  >
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`menu-link-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="text-[var(--ink)] text-base sm:text-lg sm:whitespace-nowrap underline decoration-[var(--mendl)] decoration-2 underline-offset-[6px] hover:text-[var(--oxblood)] transition-colors"
                      >
                        {name}
                        <span aria-hidden="true" className="ml-1 text-[var(--oxblood)] text-xs align-super">↗</span>
                      </a>
                    ) : (
                      <span className="text-[var(--ink)] text-base sm:text-lg sm:whitespace-nowrap">
                        {name}
                      </span>
                    )}
                    <span className="leader hidden sm:block" />
                    <span className="text-[var(--ink-soft)] text-sm italic mt-1 sm:mt-0 sm:text-right">
                      {desc}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <Ornament label="Service compris" />
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ Contact ----------------------------- */
function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Pray, complete every line of the card.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm({ name: "", email: "", message: "" });
      toast.success("Your postcard has been received.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "The post office is, regrettably, on strike."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="paper-bg py-24 border-t border-[var(--oxblood)]"
    >
      <div className="max-w-3xl mx-auto px-6">
        <ChapterTag
          numeral="V"
          act="Epilogue"
          tone="saffron"
          kicker="By Way Of Correspondence"
          title="Send A Briefing"
        />
        <div
          data-testid="contact-postcard"
          className="reveal par-avion relative bg-[var(--paper-deep)] border-[3px] border-double border-[var(--oxblood)] p-6 sm:p-14 shadow-hard"
        >
          <span
            aria-hidden="true"
            className="absolute -top-3 left-4 sm:left-6 stamp-line bg-[var(--paper)] text-[0.55rem] sm:text-[0.62rem]"
          >
            Par Avion · Air Mail · Urgent
          </span>
          <div
            className="postmark hidden sm:block"
            style={{ top: "120px", right: "120px" }}
            aria-hidden="true"
          >
            <span className="pm-top">The Bureau · Mendl's Lane</span>
            <span className="pm-bar" />
            <span className="pm-bot">{new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>
          </div>
          <img
            src={STAMP}
            alt="Postage stamp"
            data-testid="contact-stamp"
            className="absolute top-3 right-3 sm:top-6 sm:right-6 w-14 sm:w-24 h-auto rotate-[6deg] border border-[var(--oxblood)]"
          />
          <p className="overline mt-2 sm:mt-0">From, the desk of</p>
          <h3 className="font-display text-2xl sm:text-3xl text-[var(--ink)] mt-1 pr-16 sm:pr-0">
            A Prospective Client
          </h3>
          <hr className="my-6 border-t border-dotted border-[var(--oxblood)]" />

          <div
            data-testid="contact-direct"
            className="mb-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 sm:gap-y-3 font-mono-w text-sm"
          >
            <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-0">
              <span className="text-[var(--oxblood)] uppercase tracking-[0.2em] text-xs whitespace-nowrap">
                By Email
              </span>
              <span className="leader hidden sm:block" />
              <a
                data-testid="contact-email-link"
                href="mailto:Prathiksha015@gmail.com"
                className="text-[var(--ink)] hover:text-[var(--oxblood)] underline decoration-dotted underline-offset-4 sm:text-right break-all"
              >
                Prathiksha015@gmail.com
              </a>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-0">
              <span className="text-[var(--oxblood)] uppercase tracking-[0.2em] text-xs whitespace-nowrap">
                On LinkedIn
              </span>
              <span className="leader hidden sm:block" />
              <a
                data-testid="contact-linkedin-link"
                href="https://www.linkedin.com/in/prathiksha-bu-1b58901b1/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--ink)] hover:text-[var(--oxblood)] underline decoration-dotted underline-offset-4 sm:text-right break-all"
              >
                /in/prathiksha-bu
              </a>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-0 sm:col-span-2">
              <span className="text-[var(--oxblood)] uppercase tracking-[0.2em] text-xs whitespace-nowrap">
                By Appointment
              </span>
              <span className="leader hidden sm:block" />
              <a
                data-testid="contact-calendly-link"
                href="https://calendly.com/prathiksha_/intro-chat"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--ink)] hover:text-[var(--oxblood)] underline decoration-dotted underline-offset-4 sm:text-right"
              >
                Book a fifteen-minute audience ↗
              </a>
            </div>
          </div>

          <hr className="my-6 border-t border-dotted border-[var(--oxblood)]" />
          <p className="overline mb-4">Or, by postcard ↓</p>

          {submitted ? (
            <div data-testid="contact-success" className="py-8 text-center">
              <Ornament label="Posted" />
              <p className="font-chapter italic text-lg text-[var(--ink)]">
                Your briefing has been collected. A reply will arrive in due
                course — typically within two working days, possibly with
                clippings.
              </p>
              <button
                data-testid="contact-send-another"
                onClick={() => setSubmitted(false)}
                className="wax-seal mt-8"
              >
                Send another
              </button>
            </div>
          ) : (
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              noValidate
              className="space-y-7"
            >
              <div>
                <label className="overline block mb-2">
                  To · Your Name &amp; Company
                </label>
                <input
                  data-testid="contact-name"
                  className="postcard-input"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Mme. or M. ___________"
                  maxLength={120}
                  required
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  Return Address · Email
                </label>
                <input
                  data-testid="contact-email"
                  className="postcard-input"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  type="email"
                  placeholder="press@your-company.io"
                  required
                />
              </div>
              <div>
                <label className="overline block mb-2">
                  Brief · A few lines on the story
                </label>
                <textarea
                  data-testid="contact-message"
                  className="postcard-input resize-none"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={6}
                  placeholder="Dear Prathiksha, we are launching ___ and would like to land coverage in ___…"
                  required
                />
              </div>
              <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
                <span className="font-mono-w text-xs text-[var(--ink-soft)] italic">
                  Sealed with care · delivered by lobby boy
                </span>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={submitting}
                  className="wax-seal"
                >
                  {submitting ? "Posting…" : "Affix & Post"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- Footer ----------------------------- */
function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="paper-bg border-t-2 border-double border-[var(--oxblood)] py-10"
    >
      <div className="max-w-5xl mx-auto px-6 text-center">
        <Ornament label="Fin" />
        <p className="font-chapter italic text-[var(--ink-soft)] mt-2">
          Composed in the year 2026 · Prathiksha BU · The Bureau of
          Considered Communications
        </p>
        <div
          data-testid="footer-links"
          className="mt-4 flex items-center justify-center gap-5 flex-wrap font-mono-w text-xs tracking-[0.2em] uppercase"
        >
          <a
            data-testid="footer-email"
            href="mailto:Prathiksha015@gmail.com"
            className="text-[var(--ink)] hover:text-[var(--oxblood)] underline decoration-dotted underline-offset-4"
          >
            Prathiksha015@gmail.com
          </a>
          <span className="diamond" />
          <a
            data-testid="footer-linkedin"
            href="https://www.linkedin.com/in/prathiksha-bu-1b58901b1/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--ink)] hover:text-[var(--oxblood)] underline decoration-dotted underline-offset-4"
          >
            LinkedIn
          </a>
          <span className="diamond" />
          <a
            data-testid="footer-calendly"
            href="https://calendly.com/prathiksha_/intro-chat"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--ink)] hover:text-[var(--oxblood)] underline decoration-dotted underline-offset-4"
          >
            Book a call
          </a>
        </div>
        <p className="font-mono-w text-xs text-[var(--oxblood)] tracking-[0.3em] uppercase mt-4">
          No reservations · By correspondence only
        </p>
      </div>
    </footer>
  );
}

/* ----------------------------- Curtains ----------------------------- */
function Curtains() {
  const lRef = useRef(null);
  const rRef = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => {
      lRef.current && lRef.current.classList.add("done");
      rRef.current && rRef.current.classList.add("done");
    }, 2000);
    return () => clearTimeout(t);
  }, []);
  return (
    <>
      <div ref={lRef} className="curtain curtain-l" aria-hidden />
      <div ref={rRef} className="curtain curtain-r" aria-hidden />
    </>
  );
}

/* -------------------------------- App ------------------------------- */
function App() {
  useReveal();
  useEffect(() => {
    axios.get(`${API}/`).catch(() => {});
  }, []);

  return (
    <div className="App paper-bg">
      <Curtains />
      <div className="grain" aria-hidden />
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: "var(--paper-deep)",
            color: "var(--ink)",
            border: "2px solid var(--oxblood)",
            borderRadius: 0,
            fontFamily: "IBM Plex Mono, monospace",
          },
        }}
      />
      <Nav />
      <main>
        <Hero />
        <About />
        <CaseStudies />
        <PressWall />
        <MenuCard />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;
